import * as React from 'react';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import { Outlet } from 'react-router';
import Sidebar from './Sidebar';
import Header from './Header';
import { useAppDispatch, useAppSelector } from 'src/store';
import { SidebarSlice } from '../contexts/SidebarSlice';
import Drawer from 'src/components/Drawer';
import { alpha } from '@mui/material';

const drawerWidth = 300;

interface Props {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window?: () => Window;
}
const ResponsiveDrawer = (props: Props) => {
  const { window } = props;
  const sidebar = useAppSelector(s => s.sidebar);
  const dispatch = useAppDispatch();

  const container = window !== undefined ? () => window().document.body : undefined;
  
  return (
    <Box sx={{ 
      display: 'flex',
      height: '100vh',
      boxSizing: 'border-box',
      background: (theme) => theme.palette.background.default,
      position: 'relative',
      zIndex: 0,
    }}>
      <CssBaseline />
      <Box
        component="nav"
        sx={{
          width: { sm: sidebar.desktopOpen ? 0 : drawerWidth, xs: 0, md: sidebar.desktopOpen ? 0 : drawerWidth },
          flexShrink: { xs: 0, sm: 0 },
          display: { xs: 'none', sm: "none", md: "none", lg: "block", xl: "block" }
        }}
        aria-label="mailbox folders"
      >
        <Drawer
          container={container}
          variant="temporary"
          disableScrollLock
          open={sidebar.mobileOpen}
          onClose={(e, r) => { dispatch(SidebarSlice.actions.setMobileOpen(!sidebar.mobileOpen)); }}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: "block", md: "block", lg: "none", xl: "none" },
            margin: 1,
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 300, boxShadow: "none" },
          }}
        >
          <Sidebar />
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              backgroundColor: "transparent",
              boxShadow: "none",
              border: "none"
            },
          }}
          open
          disableScrollLock
          hidden={sidebar.desktopOpen}
        >
          <Sidebar />
        </Drawer>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          overflow: "hidden",
          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
          position: "relative",
          zIndex: 1100,
          marginLeft: 0,
          width: { sm: `calc(100% - ${drawerWidth}px)` }
        }}
      >
        <Box
          component="header"
          sx={{
            zIndex: 1300,
            flexShrink: 0,
            position: "relative",
            padding: 0.4,
            paddingBottom: 0,
            marginLeft: 0,
            transition: "none",
          }}
        >
          <Header />
        </Box>
        <Box
          sx={{
            flexGrow: 1,
            padding: 0.4,
            paddingTop: 0.2,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            zIndex: 1150,
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflow: "auto",
              minHeight: 0,
              display: "block",
              "&::-webkit-scrollbar": { width: 3 },
              "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.2),
                borderRadius: 1.5,
                "&:hover": {
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.4),
                }
              },
            }}
          >
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
export default ResponsiveDrawer;